import { lazy, Suspense, useEffect } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import Home from "./pages/Home";
import SignIn from "./pages/SignIn";
import Register from "./pages/Register";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import Forbidden from "./pages/Forbidden";
import Privacy from "./pages/Privacy";
import NotFound from "./pages/NotFound";
import MyTraining from "./pages/profile/MyTraining";
import Courses from "./pages/Courses";
import CourseOutline from "./pages/CourseOutline";
import AdminUsers from "./pages/admin/AdminUsers";
import AdminUserDetail from "./pages/admin/AdminUserDetail";
import { RequireAuth } from "./components/RequireAuth";
import { RequireAdmin } from "./components/RequireAdmin";
import { RequireOnboarded } from "./components/auth/RequireOnboarded";
import { RequireMemberOrAdmin } from "./components/auth/RequireMemberOrAdmin";
import { usePresenceHeartbeat } from "./lib/presence";
import { RouteSpinner } from "./components/RouteSpinner";
import { clearChunkReloadGuard } from "./lib/chunk-reload";

const Lesson = lazy(() => import("./pages/Lesson"));
const CourseComplete = lazy(() => import("./pages/CourseComplete"));
const ChapterComplete = lazy(() => import("./pages/ChapterComplete"));
const AdminOperationsGuide = lazy(() => import("./pages/admin/AdminOperationsGuide"));
const AdminInvitations = lazy(() => import("./pages/admin/AdminInvitations"));
const AdminSitemap = lazy(() => import("./pages/admin/AdminSitemap"));
const AdminCourses = lazy(() => import("./pages/admin/AdminCourses"));
const AdminAnalytics = lazy(() => import("./pages/admin/AdminAnalytics"));
const Onboarding = lazy(() => import("./pages/Onboarding"));
const CourseOnboarding = lazy(() => import("./pages/CourseOnboarding"));
const ProfileAccount = lazy(() => import("./pages/profile/ProfileAccount"));
const ProfilePrivacy = lazy(() => import("./pages/profile/ProfilePrivacy"));
const Certificate = lazy(() => import("./pages/Certificate"));
const Verify = lazy(() => import("./pages/Verify"));

// Sibling of <Routes> inside the same Suspense boundary: React only commits
// (and mounts this) once every lazy route chunk for the current render has
// resolved successfully, so this is the correct "the app just loaded fine"
// signal for clearing the chunk-reload guard — never during a still-pending
// or failed import.
// Exported (only) so the Suspense-commit timing can be regression-tested in
// isolation with a controllable React.lazy import — see
// tests/unit/frontend/App.chunk-reload-sentinel.test.tsx.
export function ChunkReloadGuardReset(): null {
  useEffect(() => {
    clearChunkReloadGuard();
  }, []);
  return null;
}

export default function App() {
  usePresenceHeartbeat();
  return (
    <Suspense fallback={<RouteSpinner />}>
      <ChunkReloadGuardReset />
      <Routes>
        {/* Public */}
        <Route path="/signin" element={<SignIn />} />
        <Route path="/register" element={<Register />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/403" element={<Forbidden />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/verify/:verifyId" element={<Verify />} />

        {/* Authenticated */}
        <Route element={<RequireAuth />}>
          {/* Always-available authenticated routes — reachable mid-onboarding.
              Account + Privacy tabs do not require an onboarded role. */}
          <Route path="/account" element={<Navigate to="/profile" replace />} />
          <Route path="/profile/account" element={<ProfileAccount />} />
          <Route path="/profile/privacy" element={<ProfilePrivacy />} />

          {/* Portal-onboarding gate — any authenticated user can reach it.
              Already-onboarded users get bounced to "/" by the page itself. */}
          <Route element={<RequireMemberOrAdmin />}>
            <Route path="/onboarding" element={<Onboarding />} />
          </Route>

          {/* Onboarded-only routes — not-yet-onboarded users get redirected to /onboarding.
              Per-course onboarding lives inside this block because the user must
              have cleared portal-onboarding before they're allowed to enroll. */}
          <Route element={<RequireOnboarded />}>
            <Route path="/" element={<Home />} />
            <Route path="/profile" element={<MyTraining />} />
            <Route path="/courses" element={<Courses />} />
            <Route path="/courses/:courseSlug" element={<CourseOutline />} />
            <Route path="/courses/:courseSlug/onboarding" element={<CourseOnboarding />} />
            {/* More-specific /complete route must come before the chapter + lesson routes */}
            <Route path="/courses/:courseSlug/complete" element={<CourseComplete />} />
            {/* Chapter-completion page — must come before the general :chapter/:lesson route */}
            <Route path="/courses/:courseSlug/:chapter/complete" element={<ChapterComplete />} />
            {/* Final-exam is course-level (no :chapter) — must come before :chapter routes */}
            <Route path="/courses/:courseSlug/final-exam" element={<Lesson finalExam />} />
            {/* Course-level certificate page — must come before :chapter routes */}
            <Route path="/courses/:courseSlug/certificate" element={<Certificate />} />
            {/* More-specific chapter-quiz route must come before the general :lesson route */}
            <Route path="/courses/:courseSlug/:chapter/chapter-quiz" element={<Lesson chapterQuiz />} />
            <Route path="/courses/:courseSlug/:chapter/:lesson" element={<Lesson />} />
            {/* Legacy redirects — no assumed default course; users pick from the catalog. */}
            <Route path="/course" element={<Navigate to="/courses" replace />} />
            <Route path="/course/:chapter/:lesson" element={<Navigate to="/courses" replace />} />
          </Route>
        </Route>

        {/* Admin (role-gated) */}
        <Route element={<RequireAdmin />}>
          <Route path="/admin" element={<AdminUsers />} />
          <Route path="/admin/user/:userId" element={<AdminUserDetail />} />
          <Route path="/admin/courses" element={<AdminCourses />} />
          <Route path="/admin/analytics" element={<AdminAnalytics />} />
          <Route path="/admin/invitations" element={<AdminInvitations />} />
          <Route path="/admin/operations-guide" element={<AdminOperationsGuide />} />
          <Route path="/admin/sitemap" element={<AdminSitemap />} />
        </Route>

        <Route path="*" element={<NotFound />} />
      </Routes>
    </Suspense>
  );
}
